import React from 'react';
import { Box, alpha, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'src/hooks';
import { setMenuOpen } from 'src/store/appSlice';
import {
  isDivMenuItem,
  isMenuItemChildren,
  isMenuItemClick,
  isMenuItemHref,
  isSubMenuItem,
  isTopMenuItem,
  SubMenuItem,
  TopMenuItem
} from './menu';
import OncoreFlexMenuRow from './OncoreFlexMenuRow';
import { NoChildrenProp } from '../common';
import OncoreFlexMenuDivider from './OncoreFlexMenuDivider';
import { useMenuItemVisibility } from './hooks';
import { TrackedButton } from '@oncore/shared';

export type Props = NoChildrenProp<{
  item: TopMenuItem | SubMenuItem;
  depth?: number;
}>;

const OncoreFlexMenuDesktopItem: React.FC<Props> = (props) => {
  const {
    item,
    depth = 0,
  } = props;
  const dispatch = useDispatch();
  const isMenuItemVisibile = useMenuItemVisibility(item);
  const navigate = useNavigate();

  const sub = isSubMenuItem(item);
  const Icon = (isTopMenuItem(item) ? (<item.icon {...item.iconProps} />) : null);
  const title = typeof item.title === 'function' ? item.title() : item.title;

  const typo = (
    <Box ml={2 + 2 * depth} mr={2} alignSelf="center">
      <Typography variant={sub ? 'body2' : 'h5'}>
        {title}
      </Typography>
    </Box>
  );

  const component = isMenuItemHref(item) ? (
    <TrackedButton
      trackedName={title}
      sx={(theme) => ({
        borderRadius: 0,
        padding: 0,
        height: '42px',
        justifyContent: 'flex-start',
        textTransform: 'none',
        letterSpacing: 0,
        width: '100%',
        color: theme.palette.primary.contrastText,
        fontWeight: theme.typography.fontWeightRegular,
        '&.depth-0': {
          fontWeight: theme.typography.fontWeightMedium
        },
        backgroundColor: location.pathname === item.href ? alpha(theme.palette.primary.contrastText, 0.23) : undefined,
      })}
      href={item.href}
      onClick={(e) => {
        e.preventDefault();
        dispatch(setMenuOpen(false));
        navigate(item.href);
      }}
    >
      {typo}
    </TrackedButton>
  ) : isMenuItemClick(item) ? (
    <TrackedButton
      trackedName={title}
      onClick={item.onClick}
      sx={(theme) => ({
        borderRadius: 0,
        padding: 0,
        height: '42px',
        justifyContent: 'flex-start',
        textTransform: 'none',
        letterSpacing: 0,
        width: '100%',
        color: theme.palette.primary.contrastText,
        fontWeight: theme.typography.fontWeightRegular,
        '&.depth-0': {
          fontWeight: theme.typography.fontWeightMedium
        }
      })}
    >
      {typo}
    </TrackedButton>
  ) : typo;

  const ren = (
    <li>
      <OncoreFlexMenuRow icon={Icon} height={isMenuItemChildren(item) ? '42px' : undefined}>
        {component}
      </OncoreFlexMenuRow>
      {isMenuItemChildren(item) && (
        <ul>
          {item.children.map((child) => isDivMenuItem(child)
            ? <OncoreFlexMenuDivider key={child.id} item={child} />
            : <OncoreFlexMenuDesktopItem key={child.id} item={child} depth={depth + 1} />)}
        </ul>
      )}
    </li>
  );

  return isMenuItemVisibile ? ren : (<></>);
};

export default OncoreFlexMenuDesktopItem;
